import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'
import configService, { Country, Currency } from '@/services/configService'

interface AppConfigState {
  // Configuration système
  systemConfig: {
    default_currency: string
    default_country: string
    default_language: string
    timezone: string
    tax_calculation: 'inclusive' | 'exclusive'
  } | null
  
  // Pays et devise sélectionnés par l'utilisateur
  selectedCountry: Country | null
  selectedCurrency: Currency | null
  
  // Données disponibles
  availableCountries: Country[]
  availableCurrencies: Currency[]
  
  // États
  loading: boolean
  error: string | null

  // Actions
  fetchSystemConfig: () => Promise<void>
  fetchAvailableCountries: () => Promise<void>
  fetchAvailableCurrencies: () => Promise<void>
  setSelectedCountry: (country: Country) => void
  setSelectedCurrency: (currency: Currency) => void
  updateSystemConfig: (config: {
    default_currency?: string
    default_country?: string
    default_language?: string
    timezone?: string
    tax_calculation?: 'inclusive' | 'exclusive'
  }) => Promise<void>
  clearError: () => void
  initializeAppConfig: () => Promise<void>
}

const useAppConfigStore = create<AppConfigState>()(
  devtools(
    persist(
      (set, get) => ({
        systemConfig: null,
        selectedCountry: null,
        selectedCurrency: null,
        availableCountries: [],
        availableCurrencies: [],
        loading: false,
        error: null,

        fetchSystemConfig: async () => {
          try {
            set({ loading: true, error: null })
            const config = await configService.config.getSystemConfig()
            set({ systemConfig: config, loading: false })
          } catch (error: any) {
            set({
              error: error.response?.data?.error || 'Erreur lors du chargement de la configuration système',
              loading: false
            })
          }
        },

        fetchAvailableCountries: async () => {
          try {
            set({ loading: true, error: null })
            const response = await configService.countries.getAll({ enabled: true, limit: 100 })
            const countries = response.data
            
            set({ availableCountries: countries, loading: false })
            
            // Si aucun pays n'est sélectionné, sélectionner le premier pays disponible
            if (!get().selectedCountry && countries.length > 0) {
              set({ selectedCountry: countries[0] })
            }
          } catch (error: any) {
            set({
              error: error.response?.data?.error || 'Erreur lors du chargement des pays',
              loading: false
            })
          }
        },

        fetchAvailableCurrencies: async () => {
          try {
            set({ loading: true, error: null })
            const response = await configService.currencies.getAll({ enabled: true, limit: 100 })
            const currencies = response.data
            
            set({ availableCurrencies: currencies, loading: false })
            
            // Si aucune devise n'est sélectionnée, sélectionner la première devise disponible
            if (!get().selectedCurrency && currencies.length > 0) {
              set({ selectedCurrency: currencies[0] })
            }
          } catch (error: any) {
            set({
              error: error.response?.data?.error || 'Erreur lors du chargement des devises',
              loading: false
            })
          }
        },

        setSelectedCountry: (country) => {
          set({ selectedCountry: country })
          
          // Mettre à jour automatiquement la devise si le pays a une devise par défaut
          const currencies = get().availableCurrencies
          const defaultCurrency = currencies.find(c => c.id === country.default_currency_id)
          if (defaultCurrency) {
            set({ selectedCurrency: defaultCurrency })
          }
        },

        setSelectedCurrency: (currency) => {
          set({ selectedCurrency: currency })
        },

        updateSystemConfig: async (config) => {
          try {
            set({ loading: true, error: null })
            await configService.config.updateSystemConfig(config)
            
            // Recharger la configuration système
            await get().fetchSystemConfig()
            
            set({ loading: false })
          } catch (error: any) {
            set({
              error: error.response?.data?.error || 'Erreur lors de la mise à jour de la configuration système',
              loading: false
            })
            throw error
          }
        },

        clearError: () => set({ error: null }),

        initializeAppConfig: async () => {
          try {
            set({ loading: true, error: null })
            
            // Charger toutes les configurations en parallèle
            await Promise.all([
              get().fetchSystemConfig(),
              get().fetchAvailableCountries(),
              get().fetchAvailableCurrencies()
            ])
            
            set({ loading: false })
          } catch (error: any) {
            set({
              error: error.response?.data?.error || 'Erreur lors de l\'initialisation de la configuration',
              loading: false
            })
          }
        }
      }),
      {
        name: 'app-config-storage',
        partialize: (state) => ({
          selectedCountry: state.selectedCountry,
          selectedCurrency: state.selectedCurrency
        })
      }
    ),
    { name: 'AppConfigStore' }
  )
)

export default useAppConfigStore
